import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api-client';
import { Plus, Search, Play, Square, RotateCcw, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';

export default function Instances() {
  const [instances, setInstances] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { api.getInstances().then(setInstances); }, []);

  const filtered = instances.filter(i => {
    if (filter !== 'all' && i.status !== filter) return false;
    if (search && !i.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleAction = async (id, action) => {
    setActionLoading(`${id}-${action}`);
    if (action === 'start') { await api.startInstance(id); setInstances(prev => prev.map(i => i.id === id ? { ...i, status: 'running' } : i)); toast.success('Instance started'); }
    if (action === 'stop') { await api.stopInstance(id); setInstances(prev => prev.map(i => i.id === id ? { ...i, status: 'stopped', cpu: 0, ram: 0 } : i)); toast.success('Instance stopped'); }
    if (action === 'restart') { await api.restartInstance(id); toast.success('Instance restarted'); }
    setActionLoading(null);
  };

  const handleDelete = async (id) => {
    await api.deleteInstance(id);
    setInstances(prev => prev.filter(i => i.id !== id));
    setDeleteConfirm(null);
    toast.success('Instance deleted');
  };

  return (
    <div data-testid="instances-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white font-mono">Instances</h1>
        <Link to="/dashboard/instances/create" data-testid="instances-create-btn" className="bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-5 py-2.5 hover:bg-emerald-400 transition-all click-scale flex items-center gap-2">
          <Plus size={16} /> New Instance
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input data-testid="instances-search" type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search instances..." className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm pl-10 pr-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" />
        </div>
        <div className="flex gap-2">
          {['all', 'running', 'stopped'].map(f => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)} className={`px-4 py-2.5 rounded-sm font-mono text-sm transition-all ${filter === f ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'border border-zinc-800 text-zinc-500 hover:border-zinc-700'}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Name', 'Status', 'Tier', 'Region', 'IP Address', 'Actions'].map(h => (
                <th key={h} className="text-left py-3 px-4 text-xs font-mono text-zinc-500 uppercase tracking-widest">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(inst => (
              <tr key={inst.id} data-testid={`instance-row-${inst.id}`} className="border-b border-zinc-800/50 hover:bg-white/[0.02] transition-all">
                <td className="py-3 px-4">
                  <Link to={`/dashboard/instances/${inst.id}`} className="text-sm font-mono text-white hover:text-emerald-400 transition-colors">{inst.name}</Link>
                </td>
                <td className="py-3 px-4">
                  <span className={`text-xs font-mono px-2 py-0.5 rounded-sm ${inst.status === 'running' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}>{inst.status}</span>
                </td>
                <td className="py-3 px-4 text-sm font-mono text-zinc-400">{inst.tier}</td>
                <td className="py-3 px-4 text-sm font-mono text-zinc-400">{inst.region}</td>
                <td className="py-3 px-4 text-sm font-mono text-zinc-500">{inst.ip}</td>
                <td className="py-3 px-4">
                  <div className="flex gap-2">
                    {inst.status === 'stopped' ? (
                      <button data-testid={`start-${inst.id}`} onClick={() => handleAction(inst.id, 'start')} disabled={!!actionLoading} className="p-1.5 rounded-sm hover:bg-emerald-500/10 text-zinc-500 hover:text-emerald-400 transition-all disabled:opacity-50"><Play size={14} /></button>
                    ) : (
                      <button data-testid={`stop-${inst.id}`} onClick={() => handleAction(inst.id, 'stop')} disabled={!!actionLoading} className="p-1.5 rounded-sm hover:bg-red-500/10 text-zinc-500 hover:text-red-400 transition-all disabled:opacity-50"><Square size={14} /></button>
                    )}
                    <button data-testid={`restart-${inst.id}`} onClick={() => handleAction(inst.id, 'restart')} disabled={!!actionLoading} className="p-1.5 rounded-sm hover:bg-cyan-500/10 text-zinc-500 hover:text-cyan-400 transition-all disabled:opacity-50"><RotateCcw size={14} /></button>
                    <button data-testid={`delete-${inst.id}`} onClick={() => setDeleteConfirm(inst.id)} className="p-1.5 rounded-sm hover:bg-red-500/10 text-zinc-500 hover:text-red-400 transition-all"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="py-12 text-center text-sm font-mono text-zinc-600">No instances found</div>
        )}
      </div>

      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white font-mono">Delete Instance</DialogTitle>
            <DialogDescription className="text-zinc-500 font-mono text-xs">This will permanently delete the instance and all its data. This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setDeleteConfirm(null)} className="flex-1 border border-zinc-800 text-zinc-400 font-mono text-sm rounded-sm py-2.5 hover:border-zinc-700 transition-all">Cancel</button>
            <button data-testid="confirm-delete-instance" onClick={() => handleDelete(deleteConfirm)} className="flex-1 bg-red-500 text-white font-mono font-bold text-sm rounded-sm py-2.5 hover:bg-red-400 transition-all">Delete Instance</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
